import type {
  Command,
  ObjectId,
  Size,
  SpatialWindow,
  Vec2,
  WindowFrame,
  WindowState,
  WorkspaceDocument,
} from '@/types';
import { createCommand } from '@/core/commands/createCommand';

type Doc = WorkspaceDocument;

const replaceWindow = (d: Doc, id: ObjectId, fn: (w: SpatialWindow) => SpatialWindow): Doc => {
  const w = d.objects[id];
  if (w?.kind !== 'window') return d;
  return { ...d, objects: { ...d.objects, [id]: fn(w) } };
};

const frameOf = (w: SpatialWindow): WindowFrame => ({ position: w.transform.position, size: w.size });

const withFrame = (w: SpatialWindow, frame: WindowFrame): SpatialWindow => ({
  ...w,
  transform: { ...w.transform, position: frame.position },
  size: frame.size,
});

/**
 * Sets a window's state, storing or restoring its frame:
 *   normal    → restore `previousFrame` if any, clear it
 *   maximized → remember the current frame, adopt `frame`
 *   minimized → keep the frame (the window collapses in place)
 * Null when the window is missing or already in that state.
 */
export const setWindowStateCommand = (
  doc: Doc,
  id: ObjectId,
  state: WindowState,
  frame?: WindowFrame,
  now = Date.now(),
): Command<Doc> | null => {
  const before = doc.objects[id];
  if (before?.kind !== 'window' || before.windowState === state) return null;
  const after: SpatialWindow = (() => {
    const touched = { ...before, windowState: state, metadata: { ...before.metadata, updatedAt: now } };
    switch (state) {
      case 'maximized':
        return withFrame(
          { ...touched, previousFrame: before.previousFrame ?? frameOf(before) },
          frame ?? frameOf(before),
        );
      case 'normal': {
        const restore = before.previousFrame;
        return restore ? withFrame({ ...touched, previousFrame: null }, restore) : { ...touched, previousFrame: null };
      }
      case 'minimized':
        return touched;
    }
  })();
  const labels: Record<WindowState, string> = {
    normal: 'Restore window',
    maximized: 'Maximize window',
    minimized: 'Minimize window',
  };
  return createCommand<Doc>({
    name: 'window.state',
    label: labels[state],
    execute: (d) => replaceWindow(d, id, () => after),
    undo: (d) => replaceWindow(d, id, () => before),
  });
};

/** Frame that fills the given world rectangle (used for maximize). */
export const frameFilling = (topLeft: Vec2, size: Size): WindowFrame => ({ position: topLeft, size });
